import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  Calendar, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Edit2,
  Trash2,
  Download,
  Eye,
  Mail,
  ExternalLink,
  Clock
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";
import FileViewer from "../shared/FileViewer";
import { openUploadInNewTab, downloadUploadFile } from "@/api/apiClient";

export default function DocumentoCard({ documento, onEditar, onExcluir }) {
  const statusConfig = {
    valido: {
      icon: CheckCircle2,
      color: "bg-green-100 text-green-700 border-green-200",
      label: "Válido"
    },
    proximo_vencimento: {
      icon: AlertTriangle,
      color: "bg-amber-100 text-amber-700 border-amber-200",
      label: "Próximo do Vencimento"
    },
    vencido: {
      icon: XCircle,
      color: "bg-red-100 text-red-700 border-red-200",
      label: "Vencido"
    }
  };

  const StatusIcon = statusConfig[documento.status]?.icon || CheckCircle2;

  const calcularDiasRestantes = () => {
    const val = documento.data_validade_final;
    if (!val || typeof val !== 'string') return null;
    try {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const dataValidade = safeParseISO(val);
      return differenceInDays(dataValidade, hoje);
    } catch {
      return null;
    }
  };

  const diasRestantes = calcularDiasRestantes();

  const formatDate = (dateString) => {
    if (dateString == null || typeof dateString !== 'string' || !dateString.trim()) return '-';
    try {
      const d = safeParseISO(dateString);
      return d ? format(d, "dd/MM/yyyy", { locale: ptBR }) : dateString;
    } catch {
      return dateString;
    }
  };

  const calcularValidadeEmMeses = () => {
    const vi = documento.data_validade_inicial;
    const vf = documento.data_validade_final;
    if (!vi || !vf || typeof vi !== 'string' || typeof vf !== 'string') return null;
    try {
      const dataInicial = safeParseISO(vi);
      const dataFinal = safeParseISO(vf);
      if (!dataInicial || !dataFinal) return null;
      const diferencaEmDias = differenceInDays(dataFinal, dataInicial);
      const meses = Math.round(diferencaEmDias / 30);
      return meses;
    } catch {
      return null;
    }
  };

  const validadeEmMeses = calcularValidadeEmMeses();

  return (
    <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur-sm">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-slate-900 mb-1">
                  {documento.descricao}
                </h3>
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  {documento.tipo && (
                    <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
                      {documento.tipo}
                    </Badge>
                  )}
                  {documento.categoria && (
                    <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                      {documento.categoria}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {documento.data_emissao && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Data de Emissão</p>
                  <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(documento.data_emissao)}
                  </p>
                </div>
              )}

              {documento.data_validade_inicial && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Validade Inicial</p>
                  <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(documento.data_validade_inicial)}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-slate-500 mb-1">Validade Final</p>
                <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(documento.data_validade_final)}
                </p>
              </div>

              {diasRestantes !== null && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Dias Restantes</p>
                  <p className={`text-sm font-bold ${
                    diasRestantes < 0 ? 'text-red-700' : 
                    diasRestantes <= 10 ? 'text-amber-700' : 
                    'text-green-700'
                  }`}>
                    {diasRestantes < 0 ? `Vencido há ${Math.abs(diasRestantes)} dias` : `${diasRestantes} dias`}
                  </p>
                </div>
              )}

              {validadeEmMeses !== null && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Validade em Meses</p>
                  <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {validadeEmMeses} {validadeEmMeses === 1 ? 'mês' : 'meses'}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-slate-500 mb-1">Anexo</p>
                <p className={`text-sm font-medium flex items-center gap-1 ${
                  documento.anexo_url ? 'text-green-700' : 'text-red-700'
                }`}>
                  {documento.anexo_url ? (
                    <>
                      <CheckCircle2 className="w-3 h-3" />
                      Anexado
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3" />
                      Sem Anexo
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="outline" className={`${statusConfig[documento.status]?.color} border flex items-center gap-1`}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig[documento.status]?.label}
              </Badge>

              {documento.modo_controle && (
                <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                  {documento.modo_controle === 'auto_renovavel'
                    ? 'Auto renovável'
                    : documento.modo_controle === 'somente_referencia'
                      ? 'Somente referência'
                      : 'Manual controlado'}
                </Badge>
              )}

              {documento.status_conformidade && (
                <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
                  Conformidade: {documento.status_conformidade}
                </Badge>
              )}

              {documento.emails_notificacao && documento.emails_notificacao.length > 0 && (
                <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {documento.emails_notificacao.length} notificação(ões)
                </Badge>
              )}
            </div>

            {documento.link_solicitacao && (
              <div className="mt-3">
                <a 
                  href={documento.link_solicitacao}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Onde Solicitar a Certidão
                </a>
              </div>
            )}

            {documento.observacoes && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-semibold text-slate-700 mb-1">Observações:</p>
                <p className="text-sm text-slate-600">{documento.observacoes}</p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 lg:w-48">
            {documento.anexo_url && (
              <div className="flex flex-col gap-2 mb-2">
                <Button
                  variant="outline"
                  onClick={() => openUploadInNewTab(documento.anexo_url)}
                  className="w-full"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Visualizar Atual
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadUploadFile(documento.anexo_url, documento.anexo_nome || 'documento')}
                  className="w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Atual
                </Button>
              </div>
            )}

            {documento.historico_anexos && documento.historico_anexos.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-semibold text-slate-600 mb-2">
                  {documento.historico_anexos.length} documento(s) anterior(es)
                </p>
              </div>
            )}
            
            <Button
              variant="outline"
              onClick={onEditar}
              className="w-full"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Editar
            </Button>

            <Button
              variant="outline"
              onClick={onExcluir}
              className="w-full border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}